import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiClock, FiEdit2, FiHome, FiPlus, FiStar, FiUser } from 'react-icons/fi';
import AddLead from '../../Lead/components/AddLead';
import LeadViewModal from '../../Lead/components/LeadViewModal';
import { parseLeadPipelineLabel, parseLeadSource } from '../../Lead/utils/leadSource';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import ServiceProjectDetailsModal from './ServiceProjectDetailsModal';
import { badgeOf } from './serviceCatalog';

const PROJECTS = `
  query ServiceProjects($serviceId: String!) {
    findProjectsByServiceId(serviceId: $serviceId) {
      id
      projectId
      name
      status
      startDate
      deadLine
      startTime
      endTime
      projectServices
      manager { firstName lastName email }
      booking {
        id
        fullName
        email
        phone
        address
        postcode
        status
        preferredDate
        message
        stage { id name }
      }
    }
  }
`;

const USES = `
  query ServiceUses($serviceId: String) {
    findAllBookingServiceRecords(serviceId: $serviceId) {
      id
      price
      frequency
      createdAt
      service { id service category }
      booking {
        id
        fullName
        email
        phone
        address
        postcode
        message
        status
        preferredDate
        recordType
        opportunityStatus
        bookingFrequency
        createdAt
        stage { name }
      }
    }
  }
`;

const REVIEWS = `
  query ServiceReviews($serviceId: String!) {
    serviceReviews(serviceId: $serviceId) {
      id
      clientName
      overallRating
      comment
      reviewSubject
      createdAt
      serviceRatings
    }
  }
`;

const AVATARS = ['#7c3aed', '#db2777', '#2563eb', '#0d9488', '#d97706', '#4f46e5'];

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '') || '?').toUpperCase();
}

function avatarColor(name) {
  const n = String(name || '').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return AVATARS[n % AVATARS.length];
}

function ago(value) {
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return '';
  const minutes = Math.round((Date.now() - time) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return days === 1 ? '1 day ago' : `${days} days ago`;
  if (days < 30) {
    const weeks = Math.round(days / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  return new Date(time).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function projectTone(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'active') return { label: 'In Progress', tone: 'progress' };
  if (value === 'complete' || value === 'completed') return { label: 'Completed', tone: 'done' };
  if (value === 'inactive') return { label: 'Cancelled', tone: 'cancel' };
  if (value === 'pending') return { label: 'Pending', tone: 'pending' };
  return { label: value ? value.replace(/_/g, ' ') : '—', tone: 'pending' };
}

function leadTone(booking) {
  const opportunity = String(booking?.opportunityStatus || '').toUpperCase();
  if (opportunity === 'WON') return { label: 'Won', tone: 'won' };
  if (opportunity === 'LOST') return { label: 'Lost', tone: 'lost' };
  if (opportunity === 'NEGOTIATION') return { label: 'Developing', tone: 'developing' };
  if (opportunity === 'PROPOSAL_SENT') return { label: 'Qualified', tone: 'qualified' };
  const status = String(booking?.status || '').toUpperCase();
  if (status === 'APPROVED') return { label: 'Qualified', tone: 'qualified' };
  if (status === 'ASSIGNED') return { label: 'Developing', tone: 'developing' };
  if (status === 'CANCELLED') return { label: 'Lost', tone: 'lost' };
  return { label: 'New', tone: 'new' };
}

function sourceTone(source) {
  const value = String(source || '').toLowerCase();
  if (value.includes('adword') || value.includes('google')) return 'ad';
  if (value.includes('refer')) return 'refer';
  if (value.includes('phone') || value.includes('call')) return 'phone';
  return 'web';
}

function leadLabel(booking) {
  return parseLeadPipelineLabel(booking?.bookingFrequency) || booking?.stage?.name || 'Enquiry';
}

export function useServiceUse(serviceId, enabled) {
  const [projects, setProjects] = useState([]);
  const [uses, setUses] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    if (!serviceId) return;
    setLoading(true);
    setError('');
    try {
      const [projectRes, useRes, reviewRes] = await Promise.allSettled([
        graphqlRequest(PROJECTS, { serviceId }),
        graphqlRequest(USES, { serviceId }),
        graphqlRequest(REVIEWS, { serviceId }),
      ]);
      if (projectRes.status === 'fulfilled') setProjects(projectRes.value?.findProjectsByServiceId || []);
      if (useRes.status === 'fulfilled') setUses(useRes.value?.findAllBookingServiceRecords || []);
      if (reviewRes.status === 'fulfilled') setReviews(reviewRes.value?.serviceReviews || []);
      const failed = [projectRes, useRes, reviewRes].find((item) => item.status === 'rejected');
      if (failed && failed.status === 'rejected') setError(failed.reason?.message || 'Some service activity could not be loaded');
    } catch (err) {
      setError(err?.message || 'Could not load this service');
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);

  const leads = useMemo(() => {
    const seen = new Set();
    return uses.filter((row) => {
      const booking = row.booking;
      if (!booking?.id || seen.has(booking.id)) return false;
      if (String(booking.recordType || 'LEAD').toUpperCase() === 'BOOKING') return false;
      seen.add(booking.id);
      return true;
    });
  }, [uses]);

  return { projects, uses, leads, reviews, loading, error, reload };
}

function PanelHead({ icon, title, count, hint, action }) {
  return (
    <header className="svc-rel-head">
      <span className="svc-rel-icon">{icon}</span>
      <div>
        <h2>
          {title}
          {count ? <em>{count}</em> : null}
        </h2>
        <p>{hint}</p>
      </div>
      {action}
    </header>
  );
}

function Person({ name, sub }) {
  return (
    <span className="svc-person">
      <i style={{ background: avatarColor(name) }}>{initials(name)}</i>
      <span>
        <strong>{name || '—'}</strong>
        {sub ? <small>{sub}</small> : null}
      </span>
    </span>
  );
}

function clientOf(project, contacts) {
  const extra = contacts.get(project?.booking?.id) || {};
  const booking = project?.booking || {};
  return {
    id: booking.id || extra.id,
    fullName: booking.fullName || extra.fullName || '',
    email: booking.email || extra.email || '',
    phone: booking.phone || extra.phone || '',
    address: booking.address || extra.address || '',
  };
}

function tierLabel(project, serviceId) {
  const rows = Array.isArray(project?.projectServices) ? project.projectServices : [];
  const match = rows.find((row) => row?.service?.id === serviceId) || (rows.length === 1 ? rows[0] : null);
  const frequency = String(match?.frequency || '').trim();
  if (!frequency || frequency.includes('|')) return '';
  return frequency;
}

function projectCategory(service, project, uses) {
  const saved = badgeOf(service);
  if (saved) return saved;
  const linked = (uses || []).find((row) => row.booking?.id && row.booking.id === project?.booking?.id);
  const fromService = badgeOf({
    category: linked?.service?.category,
    name: linked?.service?.service,
  });
  if (fromService) return fromService;
  const names = [
    project?.name,
    ...(Array.isArray(project?.projectServices) ? project.projectServices.map((row) => row?.service?.service) : []),
  ].filter(Boolean).join(' ');
  return badgeOf({ name: names }) || '—';
}

function projectPopupRow(project, contacts, uses, service, frequency) {
  const linked = (uses || []).find((row) => row.booking?.id && row.booking.id === project?.booking?.id);
  const fromUse = linked?.booking || {};
  const fromProject = project?.booking || {};
  const client = clientOf(project, contacts);
  return {
    clientName: client.fullName,
    email: client.email,
    phone: client.phone,
    address: client.address || fromUse.address,
    postcode: fromProject.postcode || fromUse.postcode,
    status: fromProject.status || fromUse.status,
    preferredDate: fromProject.preferredDate || fromUse.preferredDate,
    message: fromProject.message || fromUse.message,
    stage: fromProject.stage?.name || fromUse.stage?.name || '',
    projectId: project.projectId || project.name,
    projectUuid: project.id,
    bookingId: fromProject.id || fromUse.id,
    serviceName: service.name,
    servicePrice: linked?.price,
    serviceFrequency: frequency.get(fromProject.id) || linked?.frequency || '',
    project,
  };
}

function bookingMap(uses) {
  const map = new Map();
  uses.forEach((row) => {
    if (row.booking?.id) map.set(row.booking.id, row.booking);
  });
  return map;
}

export function ProjectPanel({ service, projects, uses, loading }) {
  const [open, setOpen] = useState(null);
  const contacts = useMemo(() => bookingMap(uses), [uses]);
  const frequency = useMemo(() => {
    const map = new Map();
    uses.forEach((row) => {
      if (row.booking?.id && row.frequency) map.set(row.booking.id, row.frequency);
    });
    return map;
  }, [uses]);

  return (
    <section className="svc-rel">
      <PanelHead
        icon={<FiHome />}
        title={`Projects for ${service.name}`}
        count={projects.length ? `${projects.length} project${projects.length === 1 ? '' : 's'}` : ''}
        hint="Active and historic projects using this service"
      />
      {loading && projects.length === 0 ? <p className="svc-empty">Loading projects…</p> : null}
      {!loading && projects.length === 0 ? <p className="svc-empty">No projects use this service yet.</p> : null}
      {projects.length ? (
        <table>
          <thead>
            <tr>
              <th>Project id</th>
              <th>Client name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Category</th>
              <th>Tier</th>
              <th>Status</th>
              <th>View</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((row) => {
              const status = projectTone(row.status);
              const client = clientOf(row, contacts);
              const tier = tierLabel(row, service.id) || frequency.get(row.booking?.id) || '';
              const category = projectCategory(service, row, uses);
              return (
                <tr key={row.id}>
                  <td>
                    <strong className="svc-code">{row.projectId || 'Project'}</strong>
                    <small>{row.name || client.address || ''}</small>
                  </td>
                  <td><Person name={client.fullName} /></td>
                  <td>{client.email || '—'}</td>
                  <td>{client.phone || '—'}</td>
                  <td><span className="svc-pill is-cat">{category}</span></td>
                  <td><span className="svc-pill">{tier || '—'}</span></td>
                  <td><span className={`svc-pill is-${status.tone}`}>{status.label}</span></td>
                  <td>
                    <button type="button" className="svc-rel-view" onClick={() => setOpen(row)}>View</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      {open ? (
        <ServiceProjectDetailsModal
          row={projectPopupRow(open, contacts, uses, service, frequency)}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
}

function asLead(row, service) {
  const booking = row.booking || {};
  return {
    id: booking.id,
    clientName: booking.fullName,
    contactNumber: booking.phone,
    contactEmail: booking.email,
    address: booking.address,
    message: booking.message,
    bookingFrequency: booking.bookingFrequency,
    source: parseLeadSource(booking.bookingFrequency),
    stage: booking.stage,
    serviceType: service.id,
    serviceName: service.name,
    createdAt: booking.createdAt,
    label: String(booking.status || 'pending').toLowerCase(),
  };
}

export function LeadPanel({ service, leads, loading, onReload }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  return (
    <section className="svc-rel">
      <PanelHead
        icon={<FiUser />}
        title={`Leads for ${service.name}`}
        count={leads.length ? `${leads.length} lead${leads.length === 1 ? '' : 's'}` : ''}
        hint="People who asked or enquired for this service"
        action={(
          <button type="button" className="svc-rel-add" onClick={() => { setEditing(null); setAdding(true); }}>
            <FiPlus /> Add lead
          </button>
        )}
      />
      {loading && leads.length === 0 ? <p className="svc-empty">Loading leads…</p> : null}
      {!loading && leads.length === 0 ? <p className="svc-empty">No leads for this service yet.</p> : null}
      {leads.length ? (
        <table>
          <thead>
            <tr>
              <th>Client name</th>
              <th>Contact name</th>
              <th>Source</th>
              <th>Status</th>
              <th>View</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((row) => {
              const booking = row.booking || {};
              const status = leadTone(booking);
              const source = parseLeadSource(booking.bookingFrequency);
              return (
                <tr key={booking.id}>
                  <td><Person name={booking.fullName} sub={leadLabel(booking)} /></td>
                  <td>
                    <strong>{booking.fullName || '—'}</strong>
                    <small>{booking.email || ''}</small>
                  </td>
                  <td><span className={`svc-pill is-${sourceTone(source)}`}>{source || '—'}</span></td>
                  <td><span className={`svc-pill is-${status.tone}`}>{status.label}</span></td>
                  <td>
                    <button type="button" className="svc-rel-view" onClick={() => setViewing(asLead(row, service))}>View</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      {viewing ? createPortal(
        <LeadViewModal
          lead={viewing}
          onClose={() => setViewing(null)}
          onEdit={(lead) => {
            setViewing(null);
            setEditing(lead);
            setAdding(true);
          }}
        />,
        document.body,
      ) : null}
      {adding ? createPortal(
        <AddLead
          leadData={editing || { serviceType: service.id }}
          onCancel={() => { setAdding(false); setEditing(null); }}
          onSave={() => {
            setAdding(false);
            setEditing(null);
            onReload?.();
          }}
        />,
        document.body,
      ) : null}
    </section>
  );
}

function reviewStars(review, serviceId) {
  const row = (review.serviceRatings || []).find((item) => item?.serviceId === serviceId);
  const rating = Number(row?.rating || review.overallRating) || 0;
  return rating;
}

export function ActivityPanel({ service, projects, uses, reviews, loading }) {
  const [all, setAll] = useState(false);
  const events = useMemo(() => {
    const projectBookings = new Set(projects.map((row) => row.booking?.id).filter(Boolean));
    const items = [];
    const contacts = bookingMap(uses);
    projects.forEach((row) => {
      const client = contacts.get(row.booking?.id) || row.booking;
      const who = client?.fullName;
      items.push({
        id: `project-${row.id}`,
        kind: 'project',
        title: `Used in ${row.projectId || row.name || 'a project'}`,
        detail: who ? `Added for ${who}` : (row.name || 'Added to a project'),
        at: row.createdAt || row.startDate || client?.createdAt,
      });
    });
    const seenBookings = new Set();
    uses.forEach((row) => {
      const booking = row.booking;
      if (!booking?.id || seenBookings.has(booking.id)) return;
      seenBookings.add(booking.id);
      const isLead = String(booking.recordType || 'LEAD').toUpperCase() !== 'BOOKING';
      if (isLead) {
        items.push({
          id: `lead-${booking.id}`,
          kind: 'lead',
          title: 'New enquiry',
          detail: booking.fullName
            ? `${booking.fullName} asked about this service`
            : 'Someone enquired for this service',
          at: booking.createdAt || row.createdAt,
        });
        return;
      }
      if (projectBookings.has(booking.id)) return;
      items.push({
        id: `use-${row.id}`,
        kind: 'project',
        title: 'Added to a booking',
        detail: booking.fullName ? `Booking for ${booking.fullName}` : 'This service was added to a booking',
        at: row.createdAt || booking.createdAt,
      });
    });
    reviews.forEach((review) => {
      const stars = reviewStars(review, service.id);
      const comment = String(review.comment || '').trim();
      items.push({
        id: `review-${review.id}`,
        kind: 'review',
        title: stars ? `New ${stars}-star review` : 'New review',
        detail: comment || (review.clientName ? `${review.clientName} left a review` : review.reviewSubject || 'Review received'),
        at: review.createdAt,
      });
    });
    const created = new Date(service.createdAt).getTime();
    const updated = new Date(service.updatedAt).getTime();
    if (Number.isFinite(created) && Number.isFinite(updated) && updated - created > 2 * 60 * 1000) {
      items.push({
        id: 'service-updated',
        kind: 'edit',
        title: 'Service updated',
        detail: 'Details, tasks, or pricing were saved',
        at: service.updatedAt,
      });
    }
    return items
      .filter((item) => item.at)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [projects, uses, reviews, service.id, service.createdAt, service.updatedAt]);

  const shown = all ? events : events.slice(0, 6);
  const icon = {
    project: <FiHome />,
    lead: <FiUser />,
    review: <FiStar />,
    edit: <FiEdit2 />,
  };

  return (
    <section className="svc-rel">
      <PanelHead
        icon={<FiClock />}
        title="Recent activity"
        hint="Latest updates and usage for this service"
        action={events.length > 6 ? (
          <button type="button" className="svc-rel-link" onClick={() => setAll((open) => !open)}>
            {all ? 'Show less' : 'View all activity'}
          </button>
        ) : null}
      />
      {loading && events.length === 0 ? <p className="svc-empty">Loading activity…</p> : null}
      {!loading && events.length === 0 ? <p className="svc-empty">No activity for this service yet.</p> : null}
      <ul className="svc-activity">
        {shown.map((item) => (
          <li key={item.id}>
            <i className={`is-${item.kind}`}>{icon[item.kind]}</i>
            <span>
              <strong>{item.title}</strong>
              <small>{item.detail}</small>
            </span>
            <time>{ago(item.at)}</time>
          </li>
        ))}
      </ul>
    </section>
  );
}
